import { useEffect, useState } from 'react'
import { ApiFehler } from '../lib/api'
import { HERKUNFT_TEXT } from '../lib/kennzahlen'
import { datum } from '../lib/laden'
import { holeZusammenlegung, type Ortsstand, type Zusammenlegung } from '../lib/warteschlangen'

/**
 * Zwei Orte zu einem machen — die Wahl und die Vorschau.
 *
 * **Nicht umkehrbar.** Seit Migration 082 zieht alles zum Sieger um, und der
 * Verlierer wird gelöscht; vorher war es ein Etikett, bei dem Fotos und
 * Merkmale am archivierten Ort hängenblieben und für die App verschwanden.
 * Was das jetzt heißt, muss vor dem Klick dastehen — deshalb die Zahlen.
 *
 * **Der Sieger wird gewählt, nicht der Reihenfolge nach genommen.** Wer
 * meldet, meldet oft den besseren Eintrag; „der gemeldete verliert" wäre eine
 * schlechte Regel. Voreingestellt ist der Ort mit mehr Fotos und Bewertungen,
 * und das ist nur ein Vorschlag.
 *
 * Zwei Bildschirme benutzen das: die Meldungsbearbeitung, wo einer der beiden
 * der gemeldete ist, und die Dublettensuche, wo es keine Meldung gibt und das
 * Paar aus der Umkreissuche kommt. Deshalb sind Überschrift und die
 * Markierung „· gemeldet" abschaltbar — die Wahl selbst ist dieselbe.
 */

/** Der Ort mit mehr Inhalt. Bei Gleichstand der ältere — er ist eingewachsen. */
export function vorschlagSieger(a: Ortsstand, b: Ortsstand): string {
  const gewicht = (o: Ortsstand) => o.fotos * 2 + o.bewertungen * 2 + o.merkmale
  if (gewicht(a) !== gewicht(b)) return gewicht(a) > gewicht(b) ? a.id : b.id
  return a.angelegt <= b.angelegt ? a.id : b.id
}

export default function Zusammenlegen({
  gemeldeterOrt,
  zwilling,
  sieger,
  setzeSieger,
  /** „· gemeldet" gilt nur dort, wo es eine Meldung gibt. */
  markiereErsten = true,
  ueberschrift = 'Zusammenlegen',
}: {
  gemeldeterOrt: string
  zwilling: string
  /** Der gewählte Sieger, oder `null` für „noch nicht entschieden". */
  sieger: string | null
  setzeSieger: (id: string) => void
  markiereErsten?: boolean
  ueberschrift?: string
}) {
  const [stand, setStand] = useState<Zusammenlegung | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  // Der Verlierer ist der andere von beiden. Steht der Sieger noch nicht fest,
  // fragen wir in der wahrscheinlicheren Richtung — die Zahlen beider Orte
  // kommen ohnehin mit, und der Vorschlag steht danach fest.
  const verlierer = sieger === gemeldeterOrt ? zwilling : gemeldeterOrt
  const zielSieger = sieger ?? zwilling

  useEffect(() => {
    let abgebrochen = false
    setFehler(null)
    void holeZusammenlegung(verlierer, zielSieger)
      .then((a) => {
        if (abgebrochen) return
        setStand(a)
        // Erst wenn die Zahlen da sind, lässt sich ein Sieger vorschlagen.
        if (!sieger) setzeSieger(vorschlagSieger(a.verlierer, a.sieger))
      })
      .catch((f) => {
        if (abgebrochen) return
        setFehler(f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler')
      })
    return () => {
      abgebrochen = true
    }
    // `sieger` bewusst in den Abhängigkeiten: Beim Tauschen dreht sich die
    // Richtung, und die Vorschau gilt dann für den anderen Verlierer.
  }, [verlierer, zielSieger, sieger, setzeSieger])

  if (fehler) {
    return (
      <div className="karte">
        <h3>{ueberschrift}</h3>
        <p className="meldung meldung-fehler" role="alert">
          {fehler}
        </p>
      </div>
    )
  }

  if (!stand) {
    return (
      <div className="karte">
        <h3>{ueberschrift}</h3>
        <p className="gedaempft">Zahlen werden geholt …</p>
      </div>
    )
  }

  // Die Antwort kennt beide Orte; welcher welcher ist, entscheidet die Wahl.
  const orte = [stand.verlierer, stand.sieger]
  const gewaehlt = orte.find((o) => o.id === sieger) ?? stand.sieger
  const anderer = orte.find((o) => o.id !== gewaehlt.id) ?? stand.verlierer
  const v = stand.vorschau

  return (
    <div className="karte">
      <h3>{ueberschrift}</h3>
      <p className="leise">
        Welcher Ort <strong>bleibt</strong>? Der andere geht in ihm auf und wird
        gelöscht. Voreingestellt ist der mit mehr Inhalt — tauschen ist erlaubt.
      </p>

      <div className="paar-orte">
        {orte.map((o) => (
          <label key={o.id} className={o.id === gewaehlt.id ? 'paar-ort gewaehlt' : 'paar-ort'}>
            <input
              type="radio"
              name="sieger"
              checked={o.id === gewaehlt.id}
              onChange={() => setzeSieger(o.id)}
            />
            <span className="paar-ort-text">
              <span className="mensch-name">
                <a
                  href={`https://kinderspiel.app/p/${o.id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={(e) => e.stopPropagation()}
                >
                  {o.name ?? 'ohne Namen'}
                </a>
                {markiereErsten && o.id === gemeldeterOrt && (
                  <span className="leise"> · gemeldet</span>
                )}
              </span>
              <span className="gedaempft">
                {[o.strasse, [o.plz, o.stadt].filter(Boolean).join(' ')]
                  .filter(Boolean)
                  .join(', ') || 'keine Adresse hinterlegt'}
              </span>
              <span className="leise">
                <span className="zahl">{o.fotos}</span> {o.fotos === 1 ? 'Foto' : 'Fotos'} ·{' '}
                <span className="zahl">{o.bewertungen}</span>{' '}
                {o.bewertungen === 1 ? 'Bewertung' : 'Bewertungen'} ·{' '}
                <span className="zahl">{o.merkmale}</span>{' '}
                {o.merkmale === 1 ? 'Merkmal' : 'Merkmale'}
              </span>
              <span className="leise">
                seit {datum(o.angelegt)} ·{' '}
                {o.herkunft ? (HERKUNFT_TEXT[o.herkunft] ?? o.herkunft) : 'Quelle unbekannt'}
              </span>
            </span>
          </label>
        ))}
      </div>

      <div className="umzug-vorschau">
        <p className="leise">
          Von <strong>{anderer.name ?? 'dem anderen Ort'}</strong> ziehen um:
        </p>
        <ul className="umzug-liste">
          <li>
            <span className="zahl">{v.fotos}</span>{' '}
            {v.fotos === 1 ? 'Foto' : 'Fotos'}
          </li>
          <li>
            <span className="zahl">{v.bewertungen}</span>{' '}
            {v.bewertungen === 1 ? 'Bewertung' : 'Bewertungen'}
          </li>
          <li>
            <span className="zahl">{v.neue_merkmale.length}</span>{' '}
            {v.neue_merkmale.length === 1 ? 'Merkmal' : 'Merkmale'}
            {v.neue_merkmale.length > 0 && (
              <span className="leise"> ({v.neue_merkmale.join(', ')})</span>
            )}
          </li>
        </ul>

        {v.doppelte_bewertungen > 0 && (
          <p className="meldung meldung-warnung">
            <span className="zahl">{v.doppelte_bewertungen}</span>{' '}
            {v.doppelte_bewertungen === 1
              ? 'Mensch hat beide Orte bewertet — die ältere Bewertung wird entfernt.'
              : 'Menschen haben beide Orte bewertet — die jeweils ältere Bewertung wird entfernt.'}
          </p>
        )}

        <p className="leise gesehen-zeile">
          <strong>Das lässt sich nicht rückgängig machen.</strong> Der andere Ort
          wird gelöscht; alte Adressen leiten künftig auf den bleibenden weiter.
          Merkmale und Stammdaten füllen nur Lücken — bei einem Widerspruch
          gewinnt immer der bleibende Ort.
        </p>
      </div>
    </div>
  )
}
